import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query'; //-tech-debt-react-query;
import { HbrBadge, HbrSpinner } from '@harbor/elements-react-standalone';
import StbModal from '../security-toolbox-ui/StbModal';
import { useReleaseNotesByFixVersion, useReleaseVersions } from '../../hooks/jira-release-notes-hooks';
import { fetchItem } from '../../support/queries';
import { JiraCloudIssueDetailResponse, ReleaseNoteIssue, ReleaseVersion } from '../../types/jira-release-notes';

interface ReleaseNotesModalProps {
  open: boolean;
  onClose: () => void;
}

const ALLOWED_TYPES = new Set(['feature', 'bug']);

const formatDate = (iso: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const formatTimestamp = (ms: string | number | null): string => {
  if (!ms) return '';
  try {
    return new Date(Number(ms)).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(ms);
  }
};

// ─── ADF Renderer ────────────────────────────────────────────────────────────

const AdfMark: React.FC<{ mark: any; children: React.ReactNode }> = ({ mark, children }) => {
  if (!mark) return <>{children}</>;
  switch (mark.type) {
    case 'strong': return <strong>{children}</strong>;
    case 'em': return <em>{children}</em>;
    case 'underline': return <u>{children}</u>;
    case 'strike': return <s>{children}</s>;
    case 'code': return <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3, fontSize: 11 }}>{children}</code>;
    case 'link': return (
      <a href={mark.attrs?.href} target="_blank" rel="noopener noreferrer" style={{ color: '#0070d2' }}>
        {children}
      </a>
    );
    case 'textColor': return <span style={{ color: mark.attrs?.color }}>{children}</span>;
    default: return <>{children}</>;
  }
};

const applyMarks = (text: string, marks: any[]): React.ReactElement => {
  if (!marks || marks.length === 0) return <>{text}</>;
  return marks.reduce<React.ReactElement>(
    (acc, mark) => <AdfMark mark={mark}>{acc}</AdfMark>,
    <>{text}</>
  );
};

// Forward declaration for recursive use — assigned after AdfNode is defined
// eslint-disable-next-line prefer-const
let AdfNodes: React.FC<{ nodes: any[] }>;

const AdfNode: React.FC<{ node: any; index?: number }> = ({ node, index = 0 }) => {
  if (!node) return null;
  const key = `${node.type}-${index}`;
  const children = node.content ? <AdfNodes nodes={node.content} /> : null;

  switch (node.type) {
    case 'doc':
      return <div>{children}</div>;

    case 'paragraph':
      return (
        <p key={key} style={{ margin: '0 0 6px', lineHeight: 1.5 }}>
          {children || <br />}
        </p>
      );

    case 'text': {
      const text = node.text ?? '';
      return <>{applyMarks(text, node.marks ?? [])}</>;
    }

    case 'hardBreak':
      return <br />;

    case 'rule':
      return <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '8px 0' }} />;

    case 'inlineCard': {
      const url = node.attrs?.url ?? '';
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#0070d2', wordBreak: 'break-all' }}>
          {url}
        </a>
      );
    }

    case 'blockCard': {
      const url = node.attrs?.url ?? '';
      return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 4, padding: '6px 10px', margin: '4px 0' }}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#0070d2', wordBreak: 'break-all' }}>
            {url}
          </a>
        </div>
      );
    }

    case 'date':
      return <span style={{ background: '#f3f4f6', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>{formatTimestamp(node.attrs?.timestamp)}</span>;

    case 'mention':
      return <span style={{ color: '#0070d2', fontWeight: 500 }}>@{node.attrs?.text ?? node.attrs?.id ?? 'unknown'}</span>;

    case 'emoji':
      return <span>{node.attrs?.text ?? node.attrs?.shortName ?? ''}</span>;

    case 'heading': {
      const level = node.attrs?.level ?? 3;
      const Tag = `h${Math.min(Math.max(level, 1), 6)}` as keyof JSX.IntrinsicElements;
      const headingStyle: React.CSSProperties = { margin: '8px 0 4px', fontSize: `${13 + (6 - level)}px`, fontWeight: 600 };
      return <Tag style={headingStyle as any}>{children}</Tag>;
    }

    case 'bulletList':
      return <ul style={{ margin: '4px 0', paddingLeft: 20 }}>{children}</ul>;

    case 'orderedList':
      return <ol style={{ margin: '4px 0', paddingLeft: 20 }}>{children}</ol>;

    case 'listItem':
      return <li style={{ marginBottom: 2 }}>{children}</li>;

    case 'codeBlock':
      return (
        <pre style={{ background: '#f3f4f6', padding: '8px 10px', borderRadius: 4, fontSize: 11, overflowX: 'auto', margin: '4px 0' }}>
          <code>{children}</code>
        </pre>
      );

    case 'blockquote':
      return (
        <blockquote style={{ borderLeft: '3px solid #d1d5db', paddingLeft: 10, margin: '4px 0', color: '#6b7280' }}>
          {children}
        </blockquote>
      );

    case 'panel': {
      const panelType = node.attrs?.panelType ?? 'info';
      const colors: Record<string, string> = { info: '#dbeafe', note: '#fef9c3', warning: '#fef2c3', error: '#fee2e2', success: '#dcfce7' };
      return (
        <div style={{ background: colors[panelType] ?? '#f3f4f6', borderRadius: 4, padding: '8px 12px', margin: '4px 0' }}>
          {children}
        </div>
      );
    }

    case 'expand':
      return (
        <details style={{ margin: '4px 0', fontSize: 12 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{node.attrs?.title ?? 'Details'}</summary>
          <div style={{ paddingTop: 4 }}>{children}</div>
        </details>
      );

    case 'table':
      return (
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12, margin: '4px 0' }}>
          <tbody>{children}</tbody>
        </table>
      );

    case 'tableRow':
      return <tr>{children}</tr>;

    case 'tableHeader':
      return <th style={{ border: '1px solid #e5e7eb', padding: '4px 8px', background: '#f9fafb', textAlign: 'left' }}>{children}</th>;

    case 'tableCell':
      return <td style={{ border: '1px solid #e5e7eb', padding: '4px 8px' }}>{children}</td>;

    case 'mediaSingle':
    case 'mediaGroup':
      return <div style={{ color: '#9ca3af', fontSize: 11, margin: '4px 0' }}>{children ?? '[media]'}</div>;

    case 'media':
      return <span style={{ color: '#9ca3af', fontSize: 11 }}>[attachment]</span>;

    default:
      // Render any unknown node's children so content isn't lost
      return children ? <>{children}</> : null;
  }
};

// eslint-disable-next-line react/display-name
AdfNodes = ({ nodes }) => (
  <>
    {(nodes ?? []).map((n, i) => (
      <AdfNode key={`${n?.type ?? 'node'}-${i}`} node={n} index={i} />
    ))}
  </>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseDate = (isoDate: string | null): Date | null => {
  if (!isoDate) return null;
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const isPastDate = (isoDate: string | null) => {
  const parsed = parseDate(isoDate);
  if (!parsed) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed < today;
};

const isFeatureOrBug = (type: string | null) => ALLOWED_TYPES.has((type || '').toLowerCase());
const isDone = (status: string | null) => (status || '').toLowerCase() === 'done';
const isNotCancelled = (status: string | null) => (status || '').toLowerCase() !== 'cancelled';

// ─── Sub-components ───────────────────────────────────────────────────────────

const VersionBadge: React.FC<{ version: ReleaseVersion }> = ({ version }) => {
  if (version.released) return <HbrBadge sentiment="success">Released</HbrBadge>;
  return <HbrBadge sentiment="danger">Coming Soon</HbrBadge>;
};

const IssueCard: React.FC<{ issue: ReleaseNoteIssue }> = ({ issue }) => {
  const raw = issue?.raw as any;
  const adf = raw?._description_adf;
  const descriptionText: string = raw?._description_text ?? '';
  const type = issue.issue_type || 'Unknown';

  return (
    <div style={{ borderRadius: 6, border: '1px solid #e5e7eb', padding: '10px 14px', marginBottom: 8, background: '#fafafa' }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Name</div>
      <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.4, marginBottom: 8 }}>
        {issue.summary || '(no summary)'}
      </div>

      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Type</div>
      <div style={{ fontSize: 13, color: '#111827', lineHeight: 1.4, marginBottom: 8 }}>
        {type}
      </div>

      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Description</div>
      <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
        {adf
          ? <AdfNode node={adf} />
          : (descriptionText || '(no description)')}
      </div>
    </div>
  );
};

// ─── Modal ───────────────────────────────────────────────────────────────────

const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({ open, onClose }) => {
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  const { data: versionsData, isLoading: versionsLoading, error: versionsError } = useReleaseVersions(
    { include_released: true, include_unreleased: true, maxResults: 50 },
    { enabled: open }
  );

  const versions = useMemo(() => {
    const allVersions = versionsData?.values ?? [];
    return allVersions.filter((v) => v.released || (!!v.releaseDate && !isPastDate(v.releaseDate)));
  }, [versionsData]);

  const defaultVersionId = useMemo(() => {
    const released = versions.filter(v => v.released);
    return released[0]?.id ?? versions[0]?.id ?? null;
  }, [versions]);

  const activeVersionId = selectedVersionId ?? defaultVersionId;

  const selectedVersion = useMemo(
    () => versions.find(v => v.id === activeVersionId) ?? null,
    [versions, activeVersionId]
  );

  const { data: notesData, isLoading: notesLoading, error: notesError } = useReleaseNotesByFixVersion(
    selectedVersion?.name ?? null,
    { maxResults: 100, issue_types: 'feature,bug' },
    { enabled: open && !!selectedVersion?.name }
  );

  const versionsErrorMessage = versionsError instanceof Error ? versionsError.message : 'Failed to load versions.';
  const notesErrorMessage = notesError instanceof Error ? notesError.message : 'Failed to load release notes for this version.';

  const filteredIssues = useMemo<ReleaseNoteIssue[]>(() => {
    const items = notesData?.issues ?? [];
    return items.filter((issue) => {
      if (!isFeatureOrBug(issue.issue_type)) return false;
      if (selectedVersion?.released) return isDone(issue.status);
      return isNotCancelled(issue.status);
    });
  }, [notesData, selectedVersion]);

  const issueKeys = useMemo(
    () => Array.from(new Set(filteredIssues.map((i) => i.key).filter(Boolean))),
    [filteredIssues]
  );

  const issueDetailQueries = useQueries({
    queries: issueKeys.map((issueKey) => ({
      queryKey: ['jira-cloud-issue-detail', issueKey],
      queryFn: () => fetchItem(`jira-cloud/issues/${issueKey}`),
      enabled: open && !!issueKey,
      retry: false,
      staleTime: 1000 * 60 * 5,
    })),
  });

  const issueDetailsByKey = useMemo<Record<string, JiraCloudIssueDetailResponse>>(() => {
    const details: Record<string, JiraCloudIssueDetailResponse> = {};
    issueDetailQueries.forEach((q, i) => {
      const data = q.data as JiraCloudIssueDetailResponse | undefined;
      const issueKey = issueKeys[i];
      if (data && issueKey) details[issueKey] = data;
    });
    return details;
  }, [issueDetailQueries, issueKeys]);

  const issuesWithDetails = useMemo(() => {
    return filteredIssues.map((issue) => {
      const detail = issueDetailsByKey[issue.key];
      if (!detail) return issue;
      return {
        ...issue,
        issue_type: detail.issue_type || issue.issue_type,
        summary: detail.summary || issue.summary,
        raw: {
          ...(issue.raw || {}),
          _description_adf: detail.description ?? null,
          _description_text: detail.description_text ?? '',
        },
      };
    });
  }, [filteredIssues, issueDetailsByKey]);

  const detailsLoading = issueDetailQueries.some((q) => q.isLoading);

  return (
    <StbModal
      open={open}
      title="Release Notes"
      size="x-large"
      role="information"
      submitLabel="Close"
      onSubmit={async () => {}}
      onClose={async () => onClose()}
    >
      <div style={{ display: 'flex', height: '65vh', gap: 0, margin: '-4px' }}>

        {/* Left: version list */}
        <div style={{ width: 220, minWidth: 220, borderRight: '1px solid #e5e7eb', overflowY: 'auto', padding: '8px 0' }}>
          {versionsLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><HbrSpinner size="medium" /></div>}
          {!versionsLoading && versionsError && <div style={{ padding: 16, fontSize: 13, color: '#b91c1c' }}>{versionsErrorMessage}</div>}
          {!versionsLoading && versions.length === 0 && <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>No versions found.</div>}
          {versions.map(v => (
            <button
              key={v.id}
              onClick={() => setSelectedVersionId(v.id)}
              style={{
                width: '100%', textAlign: 'left',
                background: activeVersionId === v.id ? '#f0f4ff' : 'transparent',
                border: 'none',
                borderLeft: activeVersionId === v.id ? '3px solid #0070d2' : '3px solid transparent',
                padding: '10px 14px', cursor: 'pointer', transition: 'background 0.1s',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', marginBottom: 4 }}>{v.name}</div>
              <div style={{ marginBottom: 4 }}><VersionBadge version={v} /></div>
              {v.releaseDate && <div style={{ fontSize: 11, color: '#6b7280' }}>{formatDate(v.releaseDate)}</div>}
            </button>
          ))}
        </div>

        {/* Right: issue list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 16px' }}>
          {selectedVersion && (
            <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{selectedVersion.name}</h3>
                <VersionBadge version={selectedVersion} />
                {selectedVersion.releaseDate && (
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    {selectedVersion.released ? 'Released' : 'Release due'} {formatDate(selectedVersion.releaseDate)}
                  </span>
                )}
                {selectedVersion.description && (
                  <span style={{ fontSize: 12, color: '#6b7280' }}>— {selectedVersion.description}</span>
                )}
              </div>
              {notesData && (
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                  {issuesWithDetails.length} issue{issuesWithDetails.length !== 1 ? 's' : ''} shown
                </div>
              )}
            </div>
          )}

          {(notesLoading || detailsLoading) && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><HbrSpinner size="large" /></div>
          )}
          {!notesLoading && notesError && (
            <div style={{ color: '#b91c1c', fontSize: 13, padding: '24px 0' }}>{notesErrorMessage}</div>
          )}
          {!notesLoading && !detailsLoading && !notesError && activeVersionId && issuesWithDetails.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: 13, padding: '24px 0' }}>No qualifying Feature/Bug issues found for this version.</div>
          )}
          {!notesLoading && !detailsLoading && !notesError && issuesWithDetails.map((issue) => (
            <IssueCard key={issue.id} issue={issue} />
          ))}
          {!activeVersionId && !versionsLoading && (
            <div style={{ color: '#6b7280', fontSize: 13, padding: '24px 0' }}>Select a version to view its release notes.</div>
          )}
        </div>
      </div>
    </StbModal>
  );
};

export default ReleaseNotesModal;





